/* eslint-disable react/no-unescaped-entities */
/**
 * Tools Tutorial — walks through every Tool (formerly "Calculator") on the
 * platform: Household Calculator, Business ProForma, Multi-Machine Capacity,
 * Sensitivity (tornado) chart, AI Business Summary, Shopping-list export.
 */
import React from 'react';
import ToolSelector from '../../components/ToolSelector';
import TutorialLayout, { Callout, StepList, TryItButton } from '../../components/TutorialLayout';

const sections = [
  {
    id: 'overview',
    title: 'What are the Tools?',
    body: (
      <>
        <p>
          Under the <strong>Tools</strong> tab you'll find every calculator, planner,
          and estimator we ship with the Academy. They are grouped by who they help
          most:
        </p>
        <ul>
          <li><strong>Household</strong> — batch cost, wet-to-dry yield, and shelf-life planning for home operators.</li>
          <li><strong>Business</strong> — ProForma statement, unit economics, multi-machine capacity, and a tornado sensitivity chart.</li>
          <li><strong>AI</strong> — one-click business summary and freezing-recipe suggestions.</li>
        </ul>
        <Callout kind="tip">
          The tools accept freeform brand & size — no need to know your exact
          model number. Type "medium home freeze dryer" or pick your brand from
          the dropdown; the math is size-based.
        </Callout>
        <TryItButton to="/calculators" label="Open the Tools hub" />
      </>
    ),
  },
  {
    id: 'household',
    title: 'Household Calculator',
    body: (
      <>
        <p>Plan a home batch end-to-end in about 90 seconds:</p>
        <StepList
          steps={[
            'Pick your freeze dryer model (18 brands and sizes, alphabetical).',
            'Add ingredients one row at a time — the calculator autoloads typical moisture % for common foods.',
            'Read the outputs: dry weight, batch cost, average shelf life (with the safety note to plan around your shortest-shelf-life ingredient).',
            'Hit "Shopping List" to download a CSV you can print or open on your phone.',
          ]}
        />
        <Callout kind="info">
          Save a batch to compare against future runs. Historical batches feed
          the AI business summary for smarter recommendations.
        </Callout>
      </>
    ),
  },
  {
    id: 'business',
    title: 'Business ProForma',
    body: (
      <>
        <p>
          A full pro-forma income statement for your freeze-dry operation. Enter
          expected batch cost, retail price, weekly cycle count, fixed costs
          (utilities, subscriptions) and machine amortization; the ProForma
          returns monthly / quarterly / annual revenue, cost of goods, gross
          margin, and net.
        </p>
        <p>
          Print or export directly — the on-screen version has a one-line legal
          disclaimer to keep the page clean.
        </p>
      </>
    ),
  },
  {
    id: 'multi-machine',
    title: 'Multi-machine capacity',
    body: (
      <>
        <p>
          Planning to scale? Tell the calculator how many machines you own (or
          want) and it models combined weekly and monthly output. It also works
          in reverse — give it your target monthly output and it tells you how
          many machines you need to hit that goal.
        </p>
        <Callout kind="tip">
          Pair this with the sensitivity chart to see whether adding a second
          machine pays back faster than raising your selling price.
        </Callout>
      </>
    ),
  },
  {
    id: 'sensitivity',
    title: 'Sensitivity — the tornado chart',
    body: (
      <>
        <p>
          The tornado chart lays each variable (selling price, food cost, cycle
          count, machine utilisation, etc.) side by side so you can see which
          levers actually matter. It's called a tornado because the biggest
          impacts land at the top as the widest bars — visually it looks like
          the funnel of a tornado.
        </p>
      </>
    ),
  },
  {
    id: 'ai-summary',
    title: 'AI business summary',
    body: (
      <>
        <p>
          One click gives you a plain-English write-up of your plan — like a
          Chief Financial Officer reading your model in 60 seconds and pointing
          out <em>challenges to plan around</em> (never "weak spots" — we keep
          the language forward-looking).
        </p>
      </>
    ),
  },
];

export default function TutorialTools() {
  return (
    <div className="min-h-screen bg-background">
      <ToolSelector />
      <TutorialLayout
        eyebrow="Tutorial"
        title="Tools — every calculator explained"
        subtitle="From the household batch calculator to the multi-machine capacity planner and the AI business summary. 6 sections, ~6 minute read."
        sections={sections}
        backTo="/calculators"
        backLabel="Back to Tools"
      />
    </div>
  );
}
